import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";
import { useLocation } from "wouter";
import { useLocale } from "@/i18n/LocaleContext";
import { buildLandingSearchQuery } from "@/lib/landing-search-cta.js";

export type SearchState = object;
type SearchStateValue = string | number | boolean | null | undefined | Array<string | number>;

export function searchStateQuery(state: SearchState | URLSearchParams): string {
  return buildLandingSearchQuery(
    state instanceof URLSearchParams
      ? state
      : state as Record<string, SearchStateValue>,
  );
}

interface SearchStateLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  state: SearchState | URLSearchParams;
  children: ReactNode;
  searchPath?: string;
}

/**
 * Expose a clean, market-local search URL to crawlers while applying optional
 * filters for an ordinary in-app click. Modified clicks retain browser link
 * behavior and open the clean URL, which remains a useful accessible fallback.
 */
export function SearchStateLink({
  state,
  searchPath = "/search",
  onClick,
  target,
  children,
  ...anchorProps
}: SearchStateLinkProps) {
  const { localizedHref } = useLocale();
  const [, navigate] = useLocation();
  const query = searchStateQuery(state);
  const searchHref = localizedHref(query ? `${searchPath}?${query}` : searchPath);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0) return;

    if (!query) return;
    const destination = localizedHref(`${searchPath}?${query}`);
    if (target === "_blank" || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      event.preventDefault();
      window.open(destination, target || "_blank", "noopener,noreferrer");
    } else {
      event.preventDefault();
      // Wouter's locale-aware Router adds /uk (and any language segment).
      // Passing the already-localized URL would strip that market prefix.
      navigate(`${searchPath}?${query}`);
    }
  };

  return (
    <a {...anchorProps} href={searchHref} target={target} onClick={handleClick}>
      {children}
    </a>
  );
}